import { createContext, Dispatch, ReactNode, useContext, useReducer } from 'react';

import {
  OnboardingNodeType,
  OnboardingStepType
} from '@/pages/members/pages/Onboarding/steps/types';
import { OnboardingSteps } from '@/pages/members/pages/Onboarding/types';
import prevSubmittedStep from '@/pages/members/pages/Onboarding/utils/firstSkippedStep';
import { MIN_VERSION, ONBOARDING_VERSIONS } from '@/pages/members/pages/Onboarding/versions';
import { TreatableTreatmentPreference } from '@/repositories/members/ProgramRepository';

export enum OnboardingContextActionType {
  NEXT_STEP = 'NEXT_STEP',
  PREV_STEP = 'PREV_STEP',
  SUBMIT = 'SUBMIT',
  TRIGGER_SUBMIT = 'TRIGGER_SUBMIT', // triggers a transient state so we can submit the form from the parent
  CLEAR_SUBMIT = 'CLEAR_SUBMIT', // clears the transient state
  UNDO_SUBMIT = 'UNDO_SUBMIT', // when the submission yields an error, we can't leave the step in a submitted state
  GO_TO_STEP = 'GO_TO_STEP', // allows going to any arbitrary step
  SKIP = 'SKIP', // marks a step as skipped
  INIT = 'INIT', // initializes the onboarding context when the user has never started the onboarding
  CONTINUE = 'CONTINUE', // continues the onboarding from where the user left off
  SET_ONBOARDING_DATA = 'SET_ONBOARDING_DATA', // sets flattened data in the onboarding context
  SET_FORM_OPTIONS = 'SET_FORM_OPTIONS', // sets form options in the onboarding context
  SET_LOGIN_LINK = 'SET_LOGIN_LINK',
  RESET = 'RESET', // resets the onboarding context to the initial state
  SET_SESSION_ID = 'SET_SESSION_ID', // sets the session ID for the onboarding
  SET_PERSON_ID = 'SET_PERSON_ID', // sets the person ID for the onboarding
  SET_STEP_ERROR = 'SET_STEP_ERROR', // sets the step error
  CLEAR_STEP_ERROR = 'CLEAR_STEP_ERROR', // clears the step error
  SET_STEP_VALID = 'SET_STEP_VALID' // sets whether the current step is valid or not
}

interface InitializedData {
  urlParams: Record<string, string>;
  httpReferrerUrl: string | null;
  currentUrl: string;
  timezone?: string;
  locale: string;
  isWebView: boolean;
  osName: string;
  browserName: string;
  deviceType: string;
}

interface ExperimentsData {
  experiment_key: string;
  uid: string;
  id: string;
  enabled: boolean;
  eligible?: boolean;
}

interface PltvData {
  pltv: number | null;
}
export interface OnboardingContextState {
  data: OnboardingPayloadData;
  initialized: boolean;
  completed: boolean;
  step: number;
  totalSteps: number;
  version: number;
  expiresAt: string | null;
  expiredAt: string | null;
  steps: OnboardingSteps;
  stepsSequence: OnboardingNodeType[];
  loginLink: string;
  sessionId: string;
  personId: string | null;
  treatabilityStatus: string | null;
  initializedData: InitializedData | null;
  formOptions: {
    treatableTreatmentPreferences?: TreatableTreatmentPreference[];
  };
  _experiment?: ExperimentsData;
  stepError: string | null;
  isCurrentStepValid: boolean;
  pltvData?: PltvData;
  recaptchaSiteKey?: string | null;
}

export type OnboardingPayloadData = Record<string, string | number | boolean | undefined>;

export type OnboardingContextAction =
  | {
      type:
        | OnboardingContextActionType.NEXT_STEP
        | OnboardingContextActionType.PREV_STEP
        | OnboardingContextActionType.RESET;
    }
  | {
      type: OnboardingContextActionType.SUBMIT | OnboardingContextActionType.UNDO_SUBMIT;
      payload: {
        nodeType: OnboardingNodeType;
        stepName: OnboardingStepType;
        data?: OnboardingPayloadData;
      };
    }
  | {
      type: OnboardingContextActionType.INIT;
      payload: {
        version: number;
        step: number;
        initializedData: InitializedData;
        sessionId: string;
        expiresAt: string | null;
        expiredAt: string | null;
        recaptchaSiteKey?: string | null;
      };
    }
  | {
      type:
        | OnboardingContextActionType.TRIGGER_SUBMIT
        | OnboardingContextActionType.CLEAR_SUBMIT
        | OnboardingContextActionType.SKIP;
      payload: {
        node: OnboardingNodeType;
        uid: OnboardingStepType;
      };
    }
  | {
      type: OnboardingContextActionType.GO_TO_STEP;
      payload: {
        goToNode: OnboardingNodeType;
        fromStep: OnboardingStepType;
      };
    }
  | {
      type: OnboardingContextActionType.CONTINUE;
      payload: OnboardingContextState;
    }
  | {
      type: OnboardingContextActionType.SET_ONBOARDING_DATA;
      payload: OnboardingPayloadData;
    }
  | {
      type: OnboardingContextActionType.SET_FORM_OPTIONS;
      payload: Record<string, unknown>;
    }
  | {
      type:
        | OnboardingContextActionType.SET_LOGIN_LINK
        | OnboardingContextActionType.SET_SESSION_ID
        | OnboardingContextActionType.SET_PERSON_ID;
      payload: string;
    }
  | {
      type: OnboardingContextActionType.SET_STEP_ERROR;
      payload: string;
    }
  | {
      type: OnboardingContextActionType.CLEAR_STEP_ERROR;
    }
  | {
      type: OnboardingContextActionType.SET_STEP_VALID;
      payload: boolean;
    };

export const OnboardingContext = createContext<OnboardingContextState>(
  {} as OnboardingContextState
);
const OnboardingDispatchContext = createContext<Dispatch<OnboardingContextAction>>(
  {} as Dispatch<OnboardingContextAction>
);

const _initialState: OnboardingContextState = {
  data: {},
  completed: false,
  step: 1,
  totalSteps: 0,
  // This is a placeholder that is replaced by backend config
  version: MIN_VERSION,
  expiresAt: null,
  expiredAt: null,
  steps: {},
  stepsSequence: [],
  initialized: true,
  loginLink: '',
  sessionId: '',
  personId: null,
  treatabilityStatus: null,
  initializedData: null,
  formOptions: {},
  stepError: null,
  isCurrentStepValid: false
};

function onboardingReducer(
  state: OnboardingContextState,
  action: OnboardingContextAction
): OnboardingContextState {
  switch (action.type) {
    case OnboardingContextActionType.INIT: {
      const steps = ONBOARDING_VERSIONS[action.payload.version].sort((x) => x.index);
      return {
        ...state,
        version: action.payload.version,
        totalSteps: steps.length,
        step: action.payload.step,
        sessionId: action.payload.sessionId,
        expiresAt: action.payload.expiresAt,
        expiredAt: action.payload.expiredAt,
        recaptchaSiteKey: action.payload.recaptchaSiteKey,
        stepsSequence: steps.map((step) => step.key),
        initializedData: action.payload.initializedData,
        steps: steps.reduce(
          (acc, step) => {
            acc[step.key] = step.elements.map((element) => ({
              condition: element.condition,
              payload: {},
              index: step.index,
              submitted: false,
              skipped: false,
              submit: false,
              options: step.options,
              ...element.component.props
            }));
            return acc;
          },
          {} as OnboardingContextState['steps']
        )
      };
    }
    case OnboardingContextActionType.SUBMIT: {
      const { nodeType: stepName, data } = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          ...data
        },
        completed: state.step === state.totalSteps,
        steps: {
          ...state.steps,
          [stepName]: state.steps[stepName]!.map((step) => {
            if (step.uid === action.payload.stepName) {
              return {
                ...step,
                submitted: true,
                submit: false,
                payload: {
                  ...step.payload,
                  ...data
                }
              };
            }
            return step;
          })
        }
      };
    }
    case OnboardingContextActionType.UNDO_SUBMIT: {
      const { nodeType: stepName } = action.payload;
      return {
        ...state,
        steps: {
          ...state.steps,
          [stepName]: state.steps[stepName]!.map((step) => {
            if (step.uid === action.payload.stepName) {
              return {
                ...step,
                submitted: false,
                submit: false
              };
            }
            return step;
          })
        }
      };
    }
    case OnboardingContextActionType.SKIP: {
      const { uid, node } = action.payload;
      return {
        ...state,
        completed: state.step === state.totalSteps,
        steps: {
          ...state.steps,
          [node]: state.steps[node]!.map((step) => {
            if (step.uid === uid) {
              return {
                ...step,
                skipped: true
              };
            }
            return step;
          })
        }
      };
    }
    case OnboardingContextActionType.TRIGGER_SUBMIT: {
      const { uid, node } = action.payload;
      return {
        ...state,
        steps: {
          ...state.steps,
          [node]: state.steps[node]!.map((step) => {
            if (step.uid === uid) {
              return {
                ...step,
                submit: true
              };
            }
            return step;
          })
        }
      };
    }
    case OnboardingContextActionType.CLEAR_SUBMIT: {
      const { uid, node } = action.payload;
      return {
        ...state,
        steps: {
          ...state.steps,
          [node]: state.steps[node]!.map((step) => {
            if (step.uid === uid) {
              return {
                ...step,
                submit: false
              };
            }
            return step;
          })
        }
      };
    }
    case OnboardingContextActionType.NEXT_STEP:
      return state.step < state.totalSteps ? { ...state, step: state.step + 1 } : state;
    case OnboardingContextActionType.PREV_STEP: {
      // this needs to account for steps that have been completely skipped by a redirect
      const prevStep =
        state.stepsSequence[prevSubmittedStep(state.step, state.stepsSequence, state.steps) - 1];
      const stepsToUndo = state.stepsSequence.slice(
        state.stepsSequence.indexOf(prevStep),
        state.step
      );
      return prevStep
        ? {
            ...state,
            step: state.stepsSequence.indexOf(prevStep) + 1,
            steps: {
              ...state.steps,
              ...stepsToUndo.reduce((acc, step) => {
                acc[step] = state.steps[step]!.map((s) => {
                  return {
                    ...s,
                    submitted: false,
                    submit: false,
                    skipped: false
                  };
                });
                return acc;
              }, {} as OnboardingSteps)
            }
          }
        : state;
    }
    case OnboardingContextActionType.GO_TO_STEP: {
      // this is like NEXT_STEP but takes can go to any arbitrary step
      const { goToNode, fromStep } = action.payload;
      // we need to mark the steps in between the originating and destination steps as skipped
      // in order to allow for the back button to work properly
      const stepsSkipped = state.stepsSequence.slice(
        state.step - 1,
        state.stepsSequence.indexOf(goToNode)
      );
      return {
        ...state,
        step: state.stepsSequence.indexOf(goToNode) + 1,
        steps: {
          ...state.steps,
          ...stepsSkipped.reduce((acc, step) => {
            acc[step] = state.steps[step]!.map((s) => {
              if (s.uid === fromStep) {
                return {
                  ...s,
                  submitted: true,
                  submit: false,
                  skipped: true
                };
              }
              return {
                ...s,
                submitted: false,
                submit: false,
                skipped: true
              };
            });
            return acc;
          }, {} as OnboardingSteps)
        }
      };
    }
    case OnboardingContextActionType.CONTINUE:
      return {
        ...state,
        ...action.payload
      };
    case OnboardingContextActionType.SET_ONBOARDING_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          ...action.payload
        }
      };
    case OnboardingContextActionType.SET_FORM_OPTIONS:
      return {
        ...state,
        formOptions: {
          ...state.formOptions,
          ...action.payload
        }
      };

    case OnboardingContextActionType.SET_LOGIN_LINK:
      return {
        ...state,
        loginLink: action.payload
      };
    case OnboardingContextActionType.RESET:
      return _initialState;
    case OnboardingContextActionType.SET_SESSION_ID:
      return {
        ...state,
        sessionId: action.payload
      };
    case OnboardingContextActionType.SET_PERSON_ID:
      return {
        ...state,
        personId: action.payload
      };
    case OnboardingContextActionType.SET_STEP_ERROR:
      return {
        ...state,
        stepError: action.payload
      };
    case OnboardingContextActionType.CLEAR_STEP_ERROR:
      return {
        ...state,
        stepError: null
      };
    case OnboardingContextActionType.SET_STEP_VALID:
      return {
        ...state,
        isCurrentStepValid: action.payload
      };
    default:
      return state;
  }
}

export function OnboardingContextProvider({
  children,
  initialState
}: {
  children: ReactNode;
  initialState?: OnboardingContextState;
}) {
  const [state, dispatch] = useReducer(onboardingReducer, initialState ?? _initialState);

  return (
    <OnboardingContext.Provider value={state}>
      <OnboardingDispatchContext.Provider value={dispatch}>
        {children}
      </OnboardingDispatchContext.Provider>
    </OnboardingContext.Provider>
  );
}

export function useOnboardingContext() {
  return useContext(OnboardingContext);
}

export function useOnboardingDispatch() {
  return useContext(OnboardingDispatchContext);
}
